<!-- NOTICE: KAPTION-INTEGRATION -->
<!--
  DashboardAppBadge - Displays badge notification for dashboard apps

  Features:
  - Shows badge count/text on app tabs
  - Hover reveals message and action buttons (max 3)
  - Auto-clears when tab is activated
-->
<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';

const props = defineProps({
  appId: {
    type: Number,
    required: true,
  },
  // Position for the hover tooltip
  position: {
    type: String,
    default: 'bottom',
    validator: value => ['top', 'bottom', 'left', 'right'].includes(value),
  },
});

const emit = defineEmits(['actionClick']);

const store = useStore();
const isHovered = ref(false);
const hoverTimeout = ref(null);

const badge = computed(() =>
  store.getters['dashboardAppBadges/getBadge'](props.appId)
);

const hasBadge = computed(() => !!badge.value);

const badgeText = computed(() => {
  if (!badge.value) return '';
  const count = badge.value.count;
  if (typeof count === 'number') {
    return count > 99 ? '99+' : String(count);
  }
  // String badge - max 3 chars
  return String(count).slice(0, 3);
});

const hasActions = computed(
  () => badge.value?.actions && badge.value.actions.length > 0
);

// Show tooltip on hover with slight delay
const onMouseEnter = () => {
  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value);
  }
  hoverTimeout.value = setTimeout(() => {
    isHovered.value = true;
  }, 200);
};

const onMouseLeave = () => {
  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value);
  }
  isHovered.value = false;
};

const onActionClick = action => {
  emit('actionClick', { appId: props.appId, action });
};

const getActionVariantClass = variant => {
  switch (variant) {
    case 'primary':
      return 'bg-woot-500 text-white hover:bg-woot-600';
    case 'danger':
      return 'bg-ruby-500 text-white hover:bg-ruby-600';
    case 'secondary':
    default:
      return 'bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3';
  }
};

onMounted(() => {
  // Cleanup timeout on unmount
});

onUnmounted(() => {
  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value);
  }
});
</script>

<template>
  <div
    v-if="hasBadge"
    class="dashboard-app-badge-wrapper relative"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <!-- Badge indicator -->
    <span
      class="dashboard-app-badge inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-semibold text-white bg-ruby-500 rounded-full"
    >
      {{ badgeText }}
    </span>

    <!-- Hover tooltip with message and actions -->
    <Transition name="fade">
      <div
        v-if="isHovered && (badge.message || hasActions)"
        class="dashboard-app-badge-tooltip absolute z-50 p-2 bg-n-solid-3 border border-n-weak rounded-lg shadow-lg min-w-[200px] max-w-[280px]"
        :class="{
          'bottom-full mb-2 left-1/2 -translate-x-1/2': position === 'top',
          'top-full mt-2 left-1/2 -translate-x-1/2': position === 'bottom',
          'right-full mr-2 top-1/2 -translate-y-1/2': position === 'left',
          'left-full ml-2 top-1/2 -translate-y-1/2': position === 'right',
        }"
      >
        <!-- Message -->
        <p v-if="badge.message" class="text-sm text-n-slate-12 mb-2">
          {{ badge.message }}
        </p>

        <!-- Action buttons -->
        <div v-if="hasActions" class="flex flex-wrap gap-1.5">
          <button
            v-for="action in badge.actions"
            :key="action.id"
            class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors"
            :class="getActionVariantClass(action.variant)"
            @click.stop="onActionClick(action)"
          >
            <FluentIcon
              v-if="action.icon"
              :icon="action.icon"
              size="12"
              class="flex-shrink-0"
            />
            <span>{{ action.label }}</span>
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
